<script setup lang="ts">
/** /actions/runs — every run of every action the viewer may see, with filters. */
import { History } from "@lucide/vue";
import { computed } from "vue";
import { sortTargets, useRunbooks, useRuns } from "@/api/runbooks";
import RunList from "@/components/actions/RunList.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import FilterChips from "@/components/common/FilterChips.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { t } from "@/copy/strings";

definePage({ meta: { title: "Action runs" } });

const runbooks = useRunbooks();
const action = useRouteQuery("action");
const target = useRouteQuery("target");
const state = useRouteQuery("state");
const mine = useRouteQuery("mine");

const runs = useRuns(() => ({ runbook: action.value || undefined, target: target.value || undefined, states: state.value ? [state.value] : undefined, mine: mine.value === "1", limit: 30 }));
const items = computed(() => runs.data.value?.pages.flatMap((p) => p.items) ?? []);
const scope = computed(() => runs.data.value?.pages[0]?.scope);

const actionOptions = computed(() => (runbooks.data.value ?? []).map((a) => ({ value: a.name, label: a.title })));
const targetOptions = computed(() => sortTargets([...new Set((runbooks.data.value ?? []).flatMap((a) => a.enabledTargets))]));
const STATE_OPTIONS = ["succeeded", "failed", "completed_with_errors", "running", "awaiting_approval", "declined", "expired"].map((s) => ({ value: s, label: t.actions.state[s] }));
const stateModel = computed({ get: () => state.value || undefined, set: (v: string | string[] | undefined) => (state.value = (v as string) ?? "") });
const actionModel = computed({ get: () => action.value || undefined, set: (v: string | undefined) => (action.value = v ?? "") });
const targetModel = computed({ get: () => target.value || undefined, set: (v: string | undefined) => (target.value = v ?? "") });
const mineModel = computed({ get: () => mine.value === "1", set: (v: boolean) => (mine.value = v ? "1" : "") });
const filtered = computed(() => Boolean(action.value || target.value || state.value || mine.value));
</script>

<template>
  <div>
    <PageHeader :title="t.actions.history" :description="t.actions.historyDescription" :crumbs="[{ label: t.actions.title, to: '/actions' }, { label: t.actions.history }]" />

    <FilterBar label="Filter runs" class="mb-5">
      <SimpleSelect v-model="actionModel" :options="actionOptions" allow-empty :empty-label="t.actions.all" aria-label="Action" class="w-full sm:w-60" />
      <SimpleSelect v-model="targetModel" :options="targetOptions" allow-empty empty-label="All targets" aria-label="Target" class="w-full sm:w-48" />
      <template v-if="scope === 'all' || mine === '1'" #end>
        <div class="flex items-center gap-2">
          <Switch id="runs-mine" v-model="mineModel" />
          <Label for="runs-mine" class="font-normal">{{ t.actions.mine }}</Label>
        </div>
      </template>
      <template #chips>
        <FilterChips v-model="stateModel" :options="STATE_OPTIONS" label="State" />
        <p v-if="scope === 'mine'" class="text-xs text-muted-foreground">{{ t.actions.othersHidden }}</p>
      </template>
    </FilterBar>

    <ListSkeleton v-if="runs.isPending.value" :rows="6" />
    <ErrorState v-else-if="runs.error.value" :error="runs.error.value" @retry="runs.refetch()" />
    <EmptyState v-else-if="items.length === 0" :icon="History" :title="filtered ? t.common.noResults : t.actions.noRuns" :description="filtered ? undefined : t.actions.noRunsHint">
      <Button as-child variant="outline"><RouterLink to="/actions">{{ t.actions.title }}</RouterLink></Button>
    </EmptyState>
    <template v-else>
      <RunList :runs="items" show-action />
      <div v-if="runs.hasNextPage.value" class="mt-4 flex justify-center">
        <Button variant="outline" size="sm" :disabled="runs.isFetchingNextPage.value" @click="runs.fetchNextPage()">{{ t.actions.loadMore }}</Button>
      </div>
    </template>
  </div>
</template>
